// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * D-SUPPORT-16 — Pioneer Rx lookup.
 *
 * Patrick, 2026-08-24: "I actually need to add a support tool my team can plug in the RX number and
 * see all json raw data in the support tools."
 *
 * The report that prompted it, from ASP on Rx 104326-2: PioneerRx says waiting for precheck, the
 * portal says Data Entry. Two very different faults produce that sentence — either the envelope
 * never arrived, or it arrived and was applied to the wrong refill — and they have different
 * owners. Until now telling them apart meant asking an engineer to run a workflow by hand.
 *
 * ── Why the comparison table is above the JSON ─────────────────────────────────────────────────
 *
 * The ask was for raw JSON, and raw JSON is here: every envelope expands to its full payload. But
 * an agent reading four payloads by eye to find which refill each one names is doing a diff a
 * computer should do. So the top of the page states plainly what PioneerRx said versus what OwlRx
 * shows, per refill, and flags the disagreement. The JSON is one click away underneath, for the
 * questions the summary does not anticipate.
 *
 * ── This page shows PHI ────────────────────────────────────────────────────────────────────────
 *
 * The envelope carries patient name, date of birth, email, allergy alerts and critical comments.
 * That was an explicit decision by the owner on 2026-08-24, taken over a redacted alternative and
 * knowing the reported bug is diagnosable without PHI. It is gated on its own permission
 * (support.pioneer.read) and every lookup is audited. The banner is not decoration: an agent who
 * opens a payload should know what they are looking at and that the lookup was recorded.
 */
import { useState } from 'react'
import { Search, FileJson, AlertTriangle, ChevronRight, ChevronDown, ShieldAlert } from 'lucide-react'
import * as api from '../../api/client'
import BackToSupportTools from './BackToSupportTools'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState, EmptyState } from '../../components/workflow/StatePills'
import SearchableSelect from '../../components/common/SearchableSelect'
import { useQuery } from '@tanstack/react-query'

/** One row of the "what does each side think" comparison, keyed by refill. */
interface RefillRow {
  refill: string
  pioneerStatus: string | null
  pioneerTx: string | null
  pioneerAt: string | null
  portalState: string | null
  portalTx: string | null
  /** The portal row's printed Rx number, so the join is visible rather than implied. */
  portalRx: string | null
  disagrees: boolean
  neverParsed: boolean
  /** Two portal rows resolved to this same fill — a real finding, surfaced rather than swallowed. */
  portalConflict: boolean
}

/**
 * Build the per-refill comparison. The envelopes are the record of what PioneerRx SENT; portal_rx
 * is what OwlRx currently SHOWS. A refill present on one side and absent on the other is as much a
 * finding as a status mismatch, so the union of both sides is walked, not just the intersection.
 */
/**
 * The fill number a PORTAL row represents.
 *
 * Patrick, 2026-08-31: "for the fill 0 that is the main, it will not print as rx-0 it will print
 * just rx number. for any fill not = 0, it will print the fill number like rx-1."
 *
 * So the suffix IS the fill number, and its absence means fill 0. This is deliberately NOT keyed on
 * rx_items.refill_number: that column does not have to agree with the printed identity, and keying
 * on it is what produced the bug this replaces — ASP 149012 has portal rows 149012 and 149012-1,
 * yet the tool reported fill 1 as "not in the portal" and collapsed both rows onto one key, so one
 * row's status silently overwrote the other's. A tool built to catch a status landing on the wrong
 * fill must not itself join on the wrong column (CLAUDE.md §5: the fill is a DISTINCT prescription).
 */
function portalFillNumber(rxNumber: string | null | undefined): string {
  if (!rxNumber) return '—'
  const m = /-(\d+)\s*$/.exec(rxNumber.trim())
  return m ? String(Number(m[1])) : '0'
}

function buildComparison(data: api.PioneerLookupResponse): RefillRow[] {
  const byRefill = new Map<string, RefillRow>()

  // Latest envelope wins per fill: envelopes come back newest-first, so only fill a slot once.
  // PioneerRx sends refill_number as its own field, so use it; fall back to the printed suffix only
  // when it is absent, so an envelope is never silently dropped.
  for (const e of data.envelopes) {
    if (!e.parsed) continue
    const k = e.parsed.refill_number !== null && e.parsed.refill_number !== undefined
      ? String(e.parsed.refill_number)
      : portalFillNumber(e.parsed.rx_number)
    if (byRefill.has(k)) continue
    byRefill.set(k, {
      refill: k,
      pioneerStatus: e.parsed.rx_status,
      pioneerTx: e.parsed.tx_status,
      pioneerAt: e.sent_on_utc ?? e.received_at,
      portalState: null, portalTx: null, portalRx: null,
      disagrees: false, neverParsed: false, portalConflict: false,
    })
  }

  for (const p of data.portal_rx) {
    // fill_number comes straight off the row (rx_fill_items.fill_number, or rx_items.refill_number
    // on a tenant without migration 285). Parsing the suffix is only the fallback — the column is
    // authoritative and cannot disagree with itself the way a derived string can.
    const k = p.fill_number !== null && p.fill_number !== undefined
      ? String(p.fill_number)
      : portalFillNumber(p.rx_number)
    const row = byRefill.get(k)
    // Do NOT overwrite a portal side that is already filled. Two portal rows resolving to the same
    // fill is itself a finding — silently letting the second win is how the previous version
    // attributed one fill's status to another, with nothing on screen to say it had happened.
    if (row) {
      if (row.portalRx && row.portalRx !== p.rx_number) { row.portalConflict = true; continue }
      row.portalState = p.state; row.portalTx = p.pioneer_tx_status; row.portalRx = p.rx_number
    }
    else byRefill.set(k, {
      refill: k, pioneerStatus: null, pioneerTx: null, pioneerAt: null,
      portalState: p.state, portalTx: p.pioneer_tx_status, portalRx: p.rx_number,
      disagrees: false, neverParsed: false, portalConflict: false,
    })
  }

  // An envelope that never parsed cannot be attributed to a refill, so it is flagged on the whole
  // lookup rather than a row — it explains a missing update wherever it lands.
  const anyUnparsed = data.envelopes.some(e => !e.parsed)

  const rows = [...byRefill.values()]
  for (const r of rows) {
    // Only claim disagreement when BOTH sides spoke. One side silent is reported as silence.
    r.disagrees = !!(r.pioneerTx && r.portalTx && r.pioneerTx.trim().toLowerCase() !== r.portalTx.trim().toLowerCase())
    r.neverParsed = anyUnparsed
  }
  return rows.sort((a, b) => a.refill.localeCompare(b.refill, undefined, { numeric: true }))
}

export default function PioneerLookupPage() {
  const [pharmacyId, setPharmacyId] = useState('')
  const [rxInput, setRxInput] = useState('')
  const [query, setQuery] = useState<{ pharmacyId: string; rx: string } | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const customers = useQuery({
    queryKey: ['support', 'customers'],
    queryFn: () => api.listSupportCustomers(),
    staleTime: 5 * 60_000,
  })

  const lookup = useQuery({
    queryKey: ['support', 'pioneer', query?.pharmacyId, query?.rx],
    queryFn: () => api.lookupPioneerEnvelopes(query!.pharmacyId, query!.rx),
    enabled: !!query,
    // Ingest history does not change while an agent reads it, and the tenant-side query is not
    // cheap. Do not refetch it because the window regained focus.
    staleTime: 2 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const rxValid = /^[0-9-]{1,32}$/.test(rxInput.trim())
  const canSearch = !!pharmacyId && rxValid

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSearch) return
    setExpanded(null)
    setQuery({ pharmacyId, rx: rxInput.trim() })
  }

  const data = lookup.data
  const rows = data ? buildComparison(data) : []
  const disagreements = rows.filter(r => r.disagrees).length

  return (
    <div style={{ padding: '18px 22px', maxWidth: '100%' }}>
      <BackToSupportTools />

      <h1 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 20, margin: '0 0 4px' }}>
        <FileJson size={19} aria-hidden="true" /> Pioneer Rx lookup
      </h1>
      <p style={{ margin: '0 0 14px', color: 'var(--color-text-mute)', fontSize: 13, maxWidth: '100%' }}>
        Every PioneerRx envelope we received for an Rx, what each one said, and what OwlRx shows for
        the same Rx. Use it when a customer reports that the portal and PioneerRx disagree.
      </p>

      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 11px', marginBottom: 14,
        border: '1px solid var(--color-warning-border, #e0b34a)', borderRadius: 6,
        background: 'var(--color-warning-bg, #fdf6e3)', fontSize: 12.5, lineHeight: 1.45,
      }}>
        <ShieldAlert size={15} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          Raw payloads contain patient information — name, date of birth, contact details and
          clinical comments. Every lookup is recorded in the audit log against your account. Open a
          payload only when the answer above is not enough.
        </span>
      </div>

      <form onSubmit={submit} style={{ display: 'flex', gap: 9, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 }}>
          Customer
          <SearchableSelect
            value={pharmacyId}
            onChange={setPharmacyId}
            ariaLabel="Customer"
            placeholder={customers.isLoading ? 'Loading…' : 'Select a customer…'}
            options={(customers.data?.customers ?? []).map(c => ({ value: c.id, label: c.name }))}
            style={{ minWidth: 260 }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 }}>
          Rx number
          <input
            value={rxInput}
            onChange={e => setRxInput(e.target.value)}
            placeholder="104326 or 104326-2"
            aria-label="Rx number"
            style={{
              padding: '7px 9px', border: '1px solid var(--color-border)', borderRadius: 6,
              font: 'inherit', fontSize: 13, minWidth: 180,
            }}
          />
        </label>

        <button type="submit" disabled={!canSearch}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 13px',
            border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, fontWeight: 600,
            cursor: canSearch ? 'pointer' : 'not-allowed', opacity: canSearch ? 1 : 0.55,
            background: 'var(--color-surface)',
          }}>
          <Search size={14} aria-hidden="true" /> Look up
        </button>

        {rxInput.trim() !== '' && !rxValid && (
          <span style={{ fontSize: 12, color: 'var(--color-danger, #b3261e)', alignSelf: 'center' }}>
            Rx numbers are digits and dashes only.
          </span>
        )}
      </form>

      {lookup.isError && <ErrorBanner message={friendlyError(lookup.error)} />}
      {lookup.isFetching && <LoadingState label="Reading the pharmacy's ingest history…" />}

      {data && !lookup.isFetching && (
        <>
          {!data.ingest_configured && (
            <EmptyState message={data.note ?? 'This pharmacy has no PioneerRx ingest tables.'} />
          )}

          {data.ingest_configured && data.envelope_count === 0 && (
            <EmptyState message={
              `No PioneerRx envelope mentioning ${data.rx} has been received for this customer. `
              + 'That points at the sending side rather than at OwlRx: if PioneerRx shows a status we never got a message for, the update never left it.'
            } />
          )}

          {data.ingest_configured && data.envelope_count > 0 && (
            <>
              <div style={{ marginBottom: 8, fontSize: 13 }}>
                <strong>{data.envelope_count}</strong> envelope{data.envelope_count === 1 ? '' : 's'} received
                {data.truncated && <span style={{ color: 'var(--color-text-mute)' }}> (showing the most recent {data.envelopes.length})</span>}
                {disagreements > 0 && (
                  <span style={{ marginLeft: 10, color: 'var(--color-danger, #b3261e)', fontWeight: 600 }}>
                    <AlertTriangle size={13} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 3 }} />
                    {disagreements} refill{disagreements === 1 ? '' : 's'} where PioneerRx and the portal disagree
                  </span>
                )}
              </div>

              {/* What each side thinks, per refill — the answer, before the payloads. */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, marginBottom: 20 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--color-border)' }}>
                    <th style={th}>Fill</th>
                    <th style={th}>PioneerRx said</th>
                    <th style={th}>Portal shows</th>
                    <th style={th}>Agrees?</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.refill} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={td}>
                        <code>{r.refill}</code>
                        {/* Show the portal row this matched. The previous version joined on a column
                            that does not carry the printed identity, so making the match visible is
                            what stops the same class of bug being invisible next time. */}
                        {r.portalRx && (
                          <span style={{ color: 'var(--color-text-mute)', fontSize: 11.5 }}> · {r.portalRx}</span>
                        )}
                      </td>
                      <td style={td}>
                        {r.pioneerTx || r.pioneerStatus
                          ? <>{r.pioneerTx ?? r.pioneerStatus}
                              {r.pioneerStatus && r.pioneerTx && (
                                <span style={{ color: 'var(--color-text-mute)' }}> · {r.pioneerStatus}</span>)}
                            </>
                          : <span style={{ color: 'var(--color-text-mute)' }}>no envelope for this refill</span>}
                      </td>
                      <td style={td}>
                        {r.portalTx || r.portalState
                          ? <>{r.portalTx ?? r.portalState}
                              {r.portalState && r.portalTx && (
                                <span style={{ color: 'var(--color-text-mute)' }}> · {r.portalState}</span>)}
                            </>
                          : <span style={{ color: 'var(--color-text-mute)' }}>not in the portal</span>}
                      </td>
                      <td style={td}>
                        {r.portalConflict
                          ? <span style={{ color: 'var(--color-danger, #b3261e)', fontWeight: 600 }}>
                              two portal rows for this fill
                            </span>
                          : r.disagrees
                          ? <span style={{ color: 'var(--color-danger, #b3261e)', fontWeight: 600 }}>disagree</span>
                          : (r.pioneerTx && r.portalTx)
                            ? <span style={{ color: 'var(--color-text-mute)' }}>match</span>
                            : <span style={{ color: 'var(--color-text-mute)' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h2 style={{ fontSize: 14, margin: '0 0 8px' }}>Envelopes</h2>
              {data.envelopes.map(e => {
                const open = expanded === e.staging_id
                return (
                  <div key={e.staging_id}
                    style={{ border: '1px solid var(--color-border)', borderRadius: 6, marginBottom: 7 }}>
                    <button
                      onClick={() => setExpanded(open ? null : e.staging_id)}
                      aria-expanded={open}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                        padding: '8px 10px', border: 'none', background: 'none', font: 'inherit',
                        fontSize: 12.5, cursor: 'pointer',
                      }}>
                      {open ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
                      <span style={{ fontWeight: 600 }}>
                        {e.sent_on_utc ? new Date(e.sent_on_utc).toLocaleString() : new Date(e.received_at).toLocaleString()}
                      </span>
                      {e.parsed
                        ? <span style={{ color: 'var(--color-text-mute)' }}>
                            refill {e.parsed.refill_number ?? '—'} · {e.parsed.tx_status ?? e.parsed.rx_status ?? 'no status'}
                          </span>
                        : <span style={{ color: 'var(--color-danger, #b3261e)', fontWeight: 600 }}>
                            never parsed{e.parse_status ? ` (${e.parse_status})` : ''} — received but never applied
                          </span>}
                      <span style={{ marginLeft: 'auto', color: 'var(--color-text-mute)', fontSize: 11.5 }}>
                        {e.message_id ? `msg ${e.message_id}` : e.sha_prefix}
                      </span>
                    </button>

                    {open && (
                      <div style={{ borderTop: '1px solid var(--color-border)', padding: '9px 11px' }}>
                        {e.parse_error && (
                          <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--color-danger, #b3261e)' }}>
                            <strong>Parse error:</strong> {e.parse_error}
                          </div>
                        )}
                        <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginBottom: 6 }}>
                          received {new Date(e.received_at).toLocaleString()}
                          {e.source_filename && <> · {e.source_filename}</>}
                          {e.workstation && <> · workstation {e.workstation}</>}
                          {e.initiating_event_id && <> · event {e.initiating_event_id}</>}
                        </div>
                        <pre style={{
                          margin: 0, padding: 10, maxHeight: 460, overflow: 'auto',
                          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
                          borderRadius: 5, fontSize: 11.5, lineHeight: 1.45,
                        }}>{prettyJson(e.envelope_json)}</pre>
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </>
      )}
    </div>
  )
}

/** Pretty-print when we can; show the original when we cannot. A payload that will not parse is
 *  itself the finding, so it must be displayed as it actually arrived rather than swallowed. */
function prettyJson(raw: string): string {
  try { return JSON.stringify(JSON.parse(raw), null, 2) } catch { return raw }
}

const th: React.CSSProperties = { padding: '6px 8px', fontWeight: 600 }
const td: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'top' }
